import type { Territory } from "$global-providers/config-provider/provider";
import type { EmitErrorEvent } from "./types";

export interface FreshdeskContext {
  user: {
    id: string;
    email: string;
    roleName: string;
    firstName: string;
    lastName: string;
    facilityName: string;
    facilityId: string;
    organisationId: string;
  };
  territory: Territory;
  environment: string;
}

const identifyUser = (context: FreshdeskContext) => {
  window.fcWidget.setExternalId(context.user.id);
  window.fcWidget.user.setFirstName(context.user.firstName);
  window.fcWidget.user.setLastName(context.user.lastName);
  window.fcWidget.user.setEmail(context.user.email);
  window.fcWidget.user.setProperties({
    territory: context.territory,
    environment: context.environment,
    role: context.user.roleName,
    clinic: context.user.facilityName,
  });
};

export const initFreshChat = async (
  {
    freshdeskToken,
    freshdeskHost,
    emitErrorEvent,
    onWidgetLoaded,
  }: {
    freshdeskToken: string;
    freshdeskHost: string;
    emitErrorEvent?: EmitErrorEvent;
    onWidgetLoaded: () => void;
  },
  userContext: FreshdeskContext,
) => {
  try {
    // Hide Freshdesk widget for e2e users as it messes with some of the tests
    if (
      userContext.user.email.match(
        /(e2e.user|e2etesting).*@elephant\.healthcare/g,
      )
    ) {
      return;
    }

    if (!window.fcWidget?.isInitialized()) {
      // init with freshchat button hidden
      window.fcWidget.init(
        {
          token: freshdeskToken,
          host: freshdeskHost,
          externalId: userContext.user.id,
          config: {
            headerProperty: {
              hideChatButton: true,
            },
          },
        },
        window.fcWidget.on("widget:loaded", () => {
          onWidgetLoaded();
        }),
      );
      identifyUser(userContext);
    }
  } catch (error: any) {
    emitErrorEvent?.({
      name: "freshdesk.init",
      error,
    });
  }
};

const freshChatId = "freshchat-js-sdk";

export const loadFreshChat = ({
  freshdeskHost,
  onLoad,
  onError,
}: {
  freshdeskHost: string;
  onLoad: () => void;
  onError: () => void;
}) => {
  if (document.getElementById(freshChatId)) return;
  // create an element to house the script - this is not the chat widget element
  const el = document.createElement("script");
  el.id = freshChatId;
  el.async = true;
  el.onload = () => onLoad();
  el.onerror = () => onError();
  el.src = `${freshdeskHost}/js/widget.js`;
  document.head.appendChild(el);
};

export const destroyFreshChat = ({
  emitErrorEvent,
}: {
  emitErrorEvent?: EmitErrorEvent;
}) => {
  const destroy = window.fcWidget?.destroy;
  if (typeof destroy === "function") {
    destroy();
  } else {
    emitErrorEvent?.({
      name: "freshdesk.destroy",
      error: new Error(
        "Error destroying freshdesk chat widget - have they updated their import script?",
      ),
    });
  }
};

// Updates settings to show freshchat button
export const showFreshChatButton = () => {
  window.fcWidget.setConfig({
    headerProperty: {
      hideChatButton: false,
    },
  });
};

// Updates settings to hide freshchat button
export const hideFreshChatButton = () => {
  window.fcWidget.setConfig({
    headerProperty: {
      hideChatButton: true,
    },
  });
};
